/**
 * Design System Constants
 *
 * Centralized design tokens for consistent spacing, styling, and component patterns.
 * Use these throughout the application instead of hardcoded values.
 */

// Spacing scale - use for consistent margins/padding
export const spacing = {
  section: 8,   // Between major page sections
  group: 6,     // Between related groups (form sections, card groups)
  item: 4,      // Between related items (buttons, inputs)
  compact: 2,   // Tight spacing (badges, tags)
};

// Border radius tokens
export const radii = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
};

// Shadow tokens (match theme.js)
export const shadows = {
  subtle: '0 1px 3px rgba(26, 22, 20, 0.06)',
  card: '0 1px 4px rgba(26, 22, 20, 0.08)',
  elevated: '0 4px 12px rgba(26, 22, 20, 0.1)',
};

// Container widths for page layouts
export const containerWidths = {
  narrow: '600px',
  standard: '900px',
  wide: '1200px',
  full: '100%',
};

// Button style mappings - combines size + variant
export const buttonStyles = {
  primary: {
    size: 'md',
    variant: 'primary',
  },
  secondary: {
    size: 'md',
    variant: 'secondary',
  },
  tertiary: {
    size: 'sm',
    variant: 'tertiary',
  },
  danger: {
    size: 'md',
    variant: 'danger',
  },
};

// Heading style mappings - ensures semantic HTML + correct typography
export const headingStyles = {
  page: {
    as: 'h1',
    size: 'xl',
    textStyle: 'h1',
  },
  section: {
    as: 'h2',
    size: 'lg',
    textStyle: 'h2',
  },
  subsection: {
    as: 'h3',
    size: 'md',
    textStyle: 'h3',
  },
  component: {
    as: 'h4',
    size: 'sm',
    textStyle: 'h4',
  },
};

// Card styling helper
export const cardStyle = {
  bg: 'white',
  border: '1px solid',
  borderColor: 'border.light',
  borderRadius: 'md',
  boxShadow: 'subtle',
  p: 6,
};

// Form field container styling
export const formContainerStyle = {
  bg: 'white',
  border: '1px solid',
  borderColor: 'border.default',
  borderRadius: 'md',
  p: 4,
};

// Helper function to get badge color for poll state
export const getStateBadgeColor = (state) => {
  const stateMap = {
    draft: 'gray',
    staged: 'orange',
    live: 'green',
    closed: 'red',
  };
  return stateMap[state] || 'gray';
};

// Stat box styling (for dashboards)
export const statBoxStyle = {
  bg: 'white',
  border: '1px solid',
  borderColor: 'border.light',
  borderRadius: 'md',
  p: 4,
};

// Usage examples:
//
// Spacing:
//   <VStack spacing={spacing.section}>  // Major sections
//   <HStack spacing={spacing.item}>     // Button groups
//
// Buttons:
//   <Button {...buttonStyles.primary}>Create</Button>
//   <Button {...buttonStyles.danger}>Delete</Button>
//
// Headings:
//   <Heading {...headingStyles.page}>Page Title</Heading>
//   <Heading {...headingStyles.subsection}>Section</Heading>
//
// Cards:
//   <Box {...cardStyle}>Content</Box>
//
// State Badges:
//   <Badge colorScheme={getStateBadgeColor(poll.state)}>{poll.state}</Badge>
